import { Backdrop, Button, ButtonBase, MenuItem, Popper, Stack } from "@mui/material"
import { useEffect, useRef, useState } from "react"
import noop from "lib/noop"
import { prevent } from "lib/prevent"
import { Bound } from "lib/@components/binding/Bound"
import { PopupContent } from "lib/@components/drop-down-button/popup-content"

export function LeftAligned({ children }) {
    return (
        <Stack sx={{ p: 1, maxWidth: "55vw" }} alignItems="flex-start">
            {children}
        </Stack>
    )
}

export function DropDownButton({
    Wrapper = MenuItem,
    ContentWrapper = LeftAligned,
    Component = Button,
    BaseComponent = ButtonBase,
    options = [],
    wrapperProps = { size: "small" },
    content = null,
    placement = "right-end",
    keepMounted = true,
    closeOnSelect = true,
    endIcon,
    endIconOpen = endIcon,
    onClick = noop,
    noOptionsText,
    ...props
}) {
    const hasOpened = useRef(false)
    const [anchorEl, setAnchorEl] = useState(null)
    const ref = useRef()
    hasOpened.current ||= Boolean(anchorEl)
    const [opacity, setOpacity] = useState(0.0001)
    options = options.filter(Boolean)
    const willShow = !!(keepMounted || Boolean(anchorEl) || hasOpened.current)
    useEffect(() => {
        if (anchorEl) {
            setTimeout(() => setOpacity(1), 160)
        }
    }, [anchorEl])
    return (
        (!!options.length || !!content) && (
            <Bound ContentWrapper={ContentWrapper}>
                <BaseComponent
                    component={Component}
                    ref={ref}
                    {...props}
                    {...(endIcon ? { endIcon: anchorEl ? endIconOpen : endIcon } : {})}
                    onClick={handleClick}
                />

                <Backdrop
                    onClick={prevent(close)}
                    onMouseDown={prevent(noop)}
                    onTouchStart={prevent(noop)}
                    sx={{ zIndex: 2100 }}
                    invisible
                    open={Boolean(anchorEl)}
                    data-cy="dropdownbutton-backdrop"
                >
                    <Popper
                        keepMounted={keepMounted}
                        modifiers={[
                            {
                                name: "flip", // Enables the menu to flip if it overflows
                                enabled: true,
                                options: {
                                    altBoundary: true,
                                    rootBoundary: "document",
                                    padding: 8,
                                },
                            },
                            {
                                name: "preventOverflow", // Prevents overflow outside the viewport
                                enabled: true,
                                options: {
                                    altAxis: true,
                                    tether: true,
                                    rootBoundary: "document",
                                    padding: 8,
                                },
                            },
                        ]}
                        sx={{ zIndex: 2201 }}
                        placement={placement}
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={() => setAnchorEl(null)}
                    >
                        {willShow && (
                            <PopupContent
                                sx={{ opacity, maxHeight: "80vh", overflowY: "auto" }}
                                content={content}
                                noOptionsText={noOptionsText}
                                optionContent={options.map((option, index) => (
                                    <Wrapper
                                        {...wrapperProps}
                                        key={option.key || index}
                                        onClick={handleMenuClick(option)}
                                        data-cy="dropdown-button-menu-item"
                                    >
                                        {option.content ?? option}
                                    </Wrapper>
                                ))}
                            />
                        )}
                    </Popper>
                </Backdrop>
            </Bound>
        )
    )

    function close() {
        setAnchorEl(null)
    }

    function handleClick() {
        setAnchorEl(ref.current)
        setTimeout(() => {
            setAnchorEl(ref.current)
        }, 150)
    }

    function handleMenuClick(option) {
        return prevent(function click() {
            if (onClick(option) !== false) {
                option.onClick?.(option)
                option.props?.onClick?.(option)
            }
            if (closeOnSelect) {
                setAnchorEl(null)
            }
        })
    }
}
